import { useMemo, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import { articleList } from "@/mocks/articles";
import { categoryList } from "@/mocks/site";
import type { Article } from "@/types/content";

const PAGE_SIZE = 6;

const articlesJsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "CollectionPage",
      name: "吃瓜资讯 - 热搜吃瓜",
      url: "https://fvwyz.cn/articles",
      inLanguage: "zh-CN",
      description: "按分类浏览热搜吃瓜的全部动态，涵盖影视剧集、综艺热点、明星动向与时尚红毯等方向。",
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://fvwyz.cn/" },
        { "@type": "ListItem", position: 2, name: "吃瓜资讯", item: "https://fvwyz.cn/articles" },
      ],
    },
  ],
};

export default function Articles() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") || "all";
  const keyword = searchParams.get("q") || "";
  const page = Math.max(1, parseInt(searchParams.get("page") || "1", 10) || 1);

  const [input, setInput] = useState(keyword);

  const articles: Article[] = articleList;

  const filtered = useMemo(() => {
    return articles.filter((item) => {
      const matchCategory = activeCategory === "all" || item.categorySlug === activeCategory;
      const q = keyword.trim();
      const matchKeyword =
        !q ||
        item.title.includes(q) ||
        item.summary.includes(q) ||
        item.category.includes(q) ||
        item.tags.some((tag) => tag.includes(q));
      return matchCategory && matchKeyword;
    });
  }, [articles, activeCategory, keyword]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const updateParams = (next: Record<string, string | null>) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(next).forEach(([key, value]) => {
      if (value === null || value === "") params.delete(key);
      else params.set(key, value);
    });
    setSearchParams(params);
  };

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    updateParams({ q: input.trim() || null, page: null });
  };

  const goPage = (target: number) => {
    updateParams({ page: target > 1 ? String(target) : null });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const activeName =
    activeCategory === "all"
      ? "全部资讯"
      : categoryList.find((c) => c.slug === activeCategory)?.name || "全部资讯";

  return (
    <SiteLayout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articlesJsonLd) }} />

      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb items={[{ label: "吃瓜资讯" }]} />
          <h1 className="mt-4 font-heading text-2xl font-black text-foreground-950 md:text-4xl">吃瓜资讯</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            按分类或关键词筛选你感兴趣的话题，每一条都附有事件概要与讨论热度，方便快速判断是否值得细看。
          </p>

          <form onSubmit={handleSearch} className="relative mt-6 max-w-xl">
            <span className="pointer-events-none absolute left-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
              <i className="ri-search-line w-5 h-5 flex items-center justify-center"></i>
            </span>
            <input
              type="search"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="输入关键词，例如：演唱会、红毯、名场面"
              aria-label="搜索资讯"
              className="h-12 w-full rounded-md border border-background-300 bg-background-50 pl-11 pr-24 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
            <button
              type="submit"
              className="absolute right-1.5 top-1.5 h-9 whitespace-nowrap rounded-md bg-primary-500 px-4 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </form>
        </div>
      </div>

      <div className="mx-auto w-full px-4 py-10 md:px-6 md:py-14">
        <div className="mb-6 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => updateParams({ category: null, page: null })}
            className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
              activeCategory === "all"
                ? "bg-foreground-950 text-background-50"
                : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
            }`}
          >
            全部
          </button>
          {categoryList.map((cat) => (
            <button
              key={cat.slug}
              type="button"
              onClick={() => updateParams({ category: cat.slug, page: null })}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                activeCategory === cat.slug
                  ? "bg-foreground-950 text-background-50"
                  : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>

        <div className="mb-5 flex flex-wrap items-center gap-3 text-sm text-foreground-500">
          <span>
            当前分类：<strong className="font-semibold text-foreground-900">{activeName}</strong>
          </span>
          {keyword && (
            <span>
              关键词：<strong className="font-semibold text-primary-600">{keyword}</strong>
            </span>
          )}
          <span>共 {filtered.length} 条内容</span>
        </div>

        {paged.length > 0 ? (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {paged.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-search-eye-line w-6 h-6 flex items-center justify-center"></i>
            </span>
            <p className="mt-4 font-heading text-base font-semibold text-foreground-900">没有找到相关内容</p>
            <p className="mt-2 text-sm text-foreground-500">换个关键词，或者切换到全部分类再看看。</p>
            <button
              type="button"
              onClick={() => {
                setInput("");
                updateParams({ category: null, q: null, page: null });
              }}
              className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              重置筛选条件
            </button>
          </div>
        )}

        {totalPages > 1 && (
          <div className="mt-10 flex items-center justify-center gap-2">
            <button
              type="button"
              disabled={currentPage === 1}
              onClick={() => goPage(currentPage - 1)}
              className="flex h-9 items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-4 text-sm font-medium text-foreground-700 transition-colors enabled:hover:border-primary-300 enabled:hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-arrow-left-s-line w-4 h-4 flex items-center justify-center"></i>
              </span>
              上一页
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
              <button
                key={num}
                type="button"
                onClick={() => goPage(num)}
                className={`h-9 w-9 rounded-md text-sm font-semibold transition-colors ${
                  num === currentPage
                    ? "bg-primary-500 text-background-50"
                    : "border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                }`}
              >
                {num}
              </button>
            ))}
            <button
              type="button"
              disabled={currentPage === totalPages}
              onClick={() => goPage(currentPage + 1)}
              className="flex h-9 items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-4 text-sm font-medium text-foreground-700 transition-colors enabled:hover:border-primary-300 enabled:hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
            >
              下一页
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
              </span>
            </button>
          </div>
        )}
      </div>
    </SiteLayout>
  );
}